import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Star, 
  Trophy, 
  Users, 
  Gamepad2, 
  Crown, 
  ArrowUp, 
  ArrowDown, 
  X, 
  Plus,
  Eye,
  EyeOff,
  Loader2,
  Trash2
} from 'lucide-react';
import { useFeaturedItemsManager, useFeaturedStats } from '@/hooks/useFeaturedItems';
import { FeaturedItemType } from '@/services/featuredItemsService';
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

// Interfaces para os tipos de dados
interface Mentor {
  id: string;
  full_name: string;
  nickname: string;
  image_url?: string;
  game_name?: string;
  total_sessions?: number;
}

interface Mentoria {
  id: string;
  title: string;
  description: string;
  price: number;
  game_name?: string;
  mentor_name?: string;
  total_purchases?: number;
}

interface Comunidade {
  id: string;
  name: string;
  description: string;
  image_url?: string;
  member_count?: number;
  game_name?: string;
}

// Hook para buscar mentores
const useMentors = (searchTerm: string, gameFilter: string) => {
  return useQuery({
    queryKey: ['mentors-selection', searchTerm, gameFilter],
    queryFn: async () => {
      console.log('🔍 [useMentors] Buscando mentores...', { searchTerm, gameFilter });
      
              // Query básica e simples para mentores
        let query = supabase
          .from('profiles')
          .select('id, nickname, image_url, preferred_game')
          .eq('role', 'mentor');

        if (searchTerm) {
          query = query.ilike('nickname', `%${searchTerm}%`);
        }

        const { data: mentors, error } = await query;
        if (error) {
          console.error('❌ [useMentors] Erro na consulta:', error);
          throw error;
        }

        console.log('✅ [useMentors] Mentores encontrados:', mentors?.length);

        if (!mentors || mentors.length === 0) {
          return [];
        }

        // Retornar dados básicos por enquanto
        const result = mentors.map(mentor => ({
          id: mentor.id,
          full_name: mentor.nickname || 'Nome não informado',
          nickname: mentor.nickname || 'Nickname não informado',
          image_url: mentor.image_url,
          game_name: mentor.preferred_game || 'Jogo não informado',
          total_sessions: 0
        }));

        console.log('✅ [useMentors] Resultado final:', result);
        return result;
    },
    retry: 2,
    retryDelay: 1000,
    staleTime: 5 * 60 * 1000 // 5 minutos
  });
};

// Hook para buscar mentorias
const useMentorias = (searchTerm: string, gameFilter: string) => {
  return useQuery({
    queryKey: ['mentorias-selection', searchTerm, gameFilter],
    queryFn: async () => {
      console.log('🔍 [useMentorias] Buscando mentorias...', { searchTerm, gameFilter });
      
              // Query básica e simples para mentorias
        let query = supabase
          .from('mentorship_offerings')
          .select('id, title, description, price_cents, game_id, mentor_id')
          .eq('is_active', true);

      if (searchTerm) {
        query = query.ilike('title', `%${searchTerm}%`);
      }

      const { data: mentorias, error } = await query;
      if (error) {
        console.error('❌ [useMentorias] Erro na consulta:', error);
        throw error;
      }

      console.log('✅ [useMentorias] Mentorias encontradas:', mentorias?.length);

              if (!mentorias || mentorias.length === 0) {
          return [];
        }

        // Buscar dados relacionados
        const { gamesMap, mentorsMap } = await fetchRelatedData();

        // Retornar dados com informações relacionadas
        const result = mentorias.map(mentoria => ({
          id: mentoria.id,
          title: mentoria.title,
          description: mentoria.description,
          price: mentoria.price_cents / 100, // Converter centavos para reais
          game_name: gamesMap.get(mentoria.game_id) || 'Jogo não informado',
          mentor_name: mentorsMap.get(mentoria.mentor_id) || 'Mentor não informado',
          total_purchases: 0
        }));

      console.log('✅ [useMentorias] Resultado final:', result);
      return result;
    },
    retry: 2,
    retryDelay: 1000,
    staleTime: 5 * 60 * 1000
  });
};

// Hook para buscar comunidades
const useComunidades = (searchTerm: string, gameFilter: string) => {
  return useQuery({
    queryKey: ['comunidades-selection', searchTerm, gameFilter],
    queryFn: async () => {
      console.log('🔍 [useComunidades] Buscando comunidades...', { searchTerm, gameFilter });
      
              // Query básica e simples para comunidades
        let query = supabase
          .from('communities')
          .select('id, name, description, cover_image_url, game_id, member_count')
          .eq('is_active', true);

      if (searchTerm) {
        query = query.ilike('name', `%${searchTerm}%`);
      }

      const { data: comunidades, error } = await query;
      if (error) {
        console.error('❌ [useComunidades] Erro na consulta:', error);
        throw error;
      }

      console.log('✅ [useComunidades] Comunidades encontradas:', comunidades?.length);

              if (!comunidades || comunidades.length === 0) {
          return [];
        }

        // Buscar dados relacionados
        const { gamesMap } = await fetchRelatedData();

        // Retornar dados com informações relacionadas
        const result = comunidades.map(comunidade => ({
          id: comunidade.id,
          name: comunidade.name,
          description: comunidade.description,
          image_url: comunidade.cover_image_url,
          member_count: comunidade.member_count || 0,
          game_name: gamesMap.get(comunidade.game_id) || 'Jogo não informado'
        }));

      console.log('✅ [useComunidades] Resultado final:', result);
      return result;
    },
    retry: 2,
    retryDelay: 1000,
    staleTime: 5 * 60 * 1000
  });
};

// Hook para buscar jogos
const useGames = () => {
  return useQuery({
    queryKey: ['games-filter'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('games')
        .select('id, name')
        .order('name');
      
      if (error) throw error;
      return data || [];
    }
  });
};

// Função auxiliar para buscar dados relacionados
const fetchRelatedData = async () => {
  const [gamesResult, mentorsResult] = await Promise.all([
    supabase.from('games').select('id, name'),
    supabase.from('profiles').select('id, nickname').eq('role', 'mentor')
  ]);

  const gamesMap = new Map();
  const mentorsMap = new Map();

  if (gamesResult.data) {
    gamesResult.data.forEach(game => gamesMap.set(game.id, game.name));
  }

  if (mentorsResult.data) {
    mentorsResult.data.forEach(mentor => mentorsMap.set(mentor.id, mentor.nickname));
  }

  return { gamesMap, mentorsMap };
};

const FeaturedItemsManager: React.FC = () => {
  const [activeTab, setActiveTab] = useState<FeaturedItemType>('mentoria');

  const mentoriaManager = useFeaturedItemsManager('mentoria');
  const mentorManager = useFeaturedItemsManager('mentor');
  const comunidadeManager = useFeaturedItemsManager('comunidade');
  const { data: stats, isLoading: statsLoading } = useFeaturedStats();

  // Estados para armazenar nomes dos itens
  const [itemNames, setItemNames] = useState<Record<string, string>>({});

  // Buscar nomes dos itens em destaque
  useEffect(() => {
    const fetchItemNames = async () => {
      const allItems = [
        ...mentoriaManager.featuredItems,
        ...mentorManager.featuredItems,
        ...comunidadeManager.featuredItems
      ];

      const names: Record<string, string> = {};

      for (const item of allItems) {
        try {
          let name = '';
          
          if (item.item_type === 'mentor') {
            const { data } = await supabase
              .from('profiles')
              .select('nickname')
              .eq('id', item.item_id)
              .single();
            name = data?.nickname || `Mentor ${item.item_id.slice(0, 8)}`;
          } else if (item.item_type === 'mentoria') {
            const { data } = await supabase
              .from('mentorship_offerings')
              .select('title')
              .eq('id', item.item_id)
              .single();
            name = data?.title || `Mentoria ${item.item_id.slice(0, 8)}`;
          } else if (item.item_type === 'comunidade') {
            const { data } = await supabase
              .from('communities')
              .select('name')
              .eq('id', item.item_id)
              .single();
            name = data?.name || `Comunidade ${item.item_id.slice(0, 8)}`;
          }

          names[item.item_id] = name;
        } catch (error) {
          console.error(`Erro ao buscar nome do ${item.item_type}:`, error);
          names[item.item_id] = `${item.item_type} ${item.item_id.slice(0, 8)}`;
        }
      }

      setItemNames(names);
    };

    if (mentoriaManager.featuredItems.length > 0 || 
        mentorManager.featuredItems.length > 0 || 
        comunidadeManager.featuredItems.length > 0) {
      fetchItemNames();
    }
  }, [mentoriaManager.featuredItems, mentorManager.featuredItems, comunidadeManager.featuredItems]);

  const getItemDisplayName = (item: any, type: FeaturedItemType) => {
    return itemNames[item.item_id] || `${type} ${item.item_id.slice(0, 8)}...`;
  };

  const getCurrentManager = () => {
    switch (activeTab) {
      case 'mentoria': return mentoriaManager;
      case 'mentor': return mentorManager;
      case 'comunidade': return comunidadeManager;
      default: return mentoriaManager;
    }
  };

  const currentManager = getCurrentManager();

  const getPositionIcon = (position: number) => {
    switch (position) {
      case 1: return <Crown className="h-4 w-4 text-yellow-500" />;
      case 2: return <Trophy className="h-4 w-4 text-gray-400" />;
      case 3: return <Star className="h-4 w-4 text-amber-600" />;
      default: return <Star className="h-4 w-4" />;
    }
  };

  const getPositionColor = (position: number) => {
    switch (position) {
      case 1: return 'bg-gradient-to-r from-yellow-500 to-yellow-600';
      case 2: return 'bg-gradient-to-r from-gray-400 to-gray-500';
      case 3: return 'bg-gradient-to-r from-amber-600 to-amber-700';
      default: return 'bg-gradient-to-r from-blue-500 to-blue-600';
    }
  };

  const handleMoveUp = (itemId: string, currentPosition: number) => {
    if (currentPosition <= 1) return;
    
    const targetItem = currentManager.featuredItems.find(item => item.position === currentPosition - 1);
    if (targetItem) {
      currentManager.swapPositions(itemId, targetItem.id);
    }
  };

  const handleMoveDown = (itemId: string, currentPosition: number) => {
    if (currentPosition >= 3) return;
    
    const targetItem = currentManager.featuredItems.find(item => item.position === currentPosition + 1);
    if (targetItem) {
      currentManager.swapPositions(itemId, targetItem.id);
    }
  };

  const handleToggleVisibility = (itemId: string, field: 'featured_on_home' | 'featured_on_page', currentValue: boolean) => {
    currentManager.updateFeatured(itemId, {
      [field]: !currentValue
    });
  };

  const handleRemoveFromFeatured = (itemId: string) => {
    if (confirm('Tem certeza que deseja remover este item dos destaques?')) {
      currentManager.removeFromFeatured(itemId);
    }
  };

  const getTabIcon = (type: FeaturedItemType) => {
    switch (type) {
      case 'mentoria': return <Gamepad2 className="h-4 w-4" />;
      case 'mentor': return <Users className="h-4 w-4" />;
      case 'comunidade': return <Star className="h-4 w-4" />;
    }
  };

  // Estados para controlar os modais
  const [mentorModalOpen, setMentorModalOpen] = useState(false);
  const [mentoriaModalOpen, setMentoriaModalOpen] = useState(false);
  const [comunidadeModalOpen, setComunidadeModalOpen] = useState(false);

  const handleAddMentor = (mentorId: string) => {
    const nextPosition = getNextAvailablePosition(mentorManager.featuredItems);
    if (nextPosition) {
      mentorManager.addToFeatured(mentorId, nextPosition, {
        featured_on_home: true,
        featured_on_page: true
      });
      setMentorModalOpen(false);
    } else {
      toast.error('Já existem 3 mentores em destaque. Remova um para adicionar outro.');
    }
  };

  const handleAddMentoria = (mentoriaId: string) => {
    const nextPosition = getNextAvailablePosition(mentoriaManager.featuredItems);
    if (nextPosition) {
      mentoriaManager.addToFeatured(mentoriaId, nextPosition, {
        featured_on_home: true,
        featured_on_page: true
      });
      setMentoriaModalOpen(false);
    } else {
      toast.error('Já existem 3 mentorias em destaque. Remova uma para adicionar outra.');
    }
  };

  const handleAddComunidade = (comunidadeId: string) => {
    const nextPosition = getNextAvailablePosition(comunidadeManager.featuredItems);
    if (nextPosition) {
      comunidadeManager.addToFeatured(comunidadeId, nextPosition, {
        featured_on_home: true,
        featured_on_page: true
      });
      setComunidadeModalOpen(false);
    } else {
      toast.error('Já existem 3 comunidades em destaque. Remova uma para adicionar outra.');
    }
  };

  const getNextAvailablePosition = (items: any[]): 1 | 2 | 3 | null => {
    const positions = items.map(item => item.position);
    for (let pos = 1; pos <= 3; pos++) {
      if (!positions.includes(pos)) {
        return pos as 1 | 2 | 3;
      }
    }
    return null;
  };

  if (statsLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header com Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-[#181D24] border-[#222A35]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#00B6F0]/20 rounded-lg">
                <Star className="h-5 w-5 text-[#00B6F0]" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Total Destaques</p>
                <p className="text-2xl font-bold text-white">
                  {statsLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : stats?.total || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#181D24] border-[#222A35]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-500/20 rounded-lg">
                <Gamepad2 className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Mentorias</p>
                <p className="text-2xl font-bold text-white">
                  {statsLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : stats?.mentoria || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#181D24] border-[#222A35]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-500/20 rounded-lg">
                <Users className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Mentores</p>
                <p className="text-2xl font-bold text-white">
                  {statsLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : stats?.mentor || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-[#181D24] border-[#222A35]">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <Star className="h-5 w-5 text-orange-500" />
              </div>
              <div>
                <p className="text-sm text-gray-400">Comunidades</p>
                <p className="text-2xl font-bold text-white">
                  {statsLoading ? <Loader2 className="h-5 w-5 animate-spin" /> : stats?.comunidade || 0}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Gerenciamento de Destaques */}
      <Card className="bg-[#181D24] border-[#222A35]">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Star className="h-5 w-5 text-[#00B6F0]" />
            Gerenciar Destaques
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as FeaturedItemType)}>
            <TabsList className="grid w-full grid-cols-3 bg-[#242C37]">
              <TabsTrigger 
                value="mentoria" 
                className="data-[state=active]:bg-[#00B6F0] data-[state=active]:text-black"
              >
                <div className="flex items-center gap-2">
                  {getTabIcon('mentoria')}
                  Mentorias
                </div>
              </TabsTrigger>
              <TabsTrigger 
                value="mentor"
                className="data-[state=active]:bg-[#00B6F0] data-[state=active]:text-black"
              >
                <div className="flex items-center gap-2">
                  {getTabIcon('mentor')}
                  Mentores
                </div>
              </TabsTrigger>
              <TabsTrigger 
                value="comunidade"
                className="data-[state=active]:bg-[#00B6F0] data-[state=active]:text-black"
              >
                <div className="flex items-center gap-2">
                  {getTabIcon('comunidade')}
                  Comunidades
                </div>
              </TabsTrigger>
            </TabsList>

            {(['mentoria', 'mentor', 'comunidade'] as FeaturedItemType[]).map((type) => (
              <TabsContent key={type} value={type} className="mt-6">
                <FeaturedItemsList
                  type={type}
                  manager={type === 'mentoria' ? mentoriaManager : type === 'mentor' ? mentorManager : comunidadeManager}
                  onMoveUp={handleMoveUp}
                  onMoveDown={handleMoveDown}
                  onToggleVisibility={handleToggleVisibility}
                  onRemove={handleRemoveFromFeatured}
                  getPositionIcon={getPositionIcon}
                  getPositionColor={getPositionColor}
                  getItemDisplayName={getItemDisplayName}
                  onOpenModal={() => {
                    if (type === 'mentor') setMentorModalOpen(true);
                    else if (type === 'mentoria') setMentoriaModalOpen(true);
                    else if (type === 'comunidade') setComunidadeModalOpen(true);
                  }}
                />
              </TabsContent>
            ))}
          </Tabs>
        </CardContent>
      </Card>

      {/* Modais de Seleção */}
      <MentorSelectionModal
        isOpen={mentorModalOpen}
        onClose={() => setMentorModalOpen(false)}
        onSelect={handleAddMentor}
      />

      <MentoriaSelectionModal
        isOpen={mentoriaModalOpen}
        onClose={() => setMentoriaModalOpen(false)}
        onSelect={handleAddMentoria}
      />

      <ComunidadeSelectionModal
        isOpen={comunidadeModalOpen}
        onClose={() => setComunidadeModalOpen(false)}
        onSelect={handleAddComunidade}
      />
    </div>
  );
};

// Componente para listar itens em destaque
interface FeaturedItemsListProps {
  type: FeaturedItemType;
  manager: ReturnType<typeof useFeaturedItemsManager>;
  onMoveUp: (itemId: string, position: number) => void;
  onMoveDown: (itemId: string, position: number) => void;
  onToggleVisibility: (itemId: string, field: 'featured_on_home' | 'featured_on_page', currentValue: boolean) => void;
  onRemove: (itemId: string) => void;
  getPositionIcon: (position: number) => React.ReactNode;
  getPositionColor: (position: number) => string;
  getItemDisplayName: (item: any, type: FeaturedItemType) => string;
  onOpenModal: () => void;
}

const FeaturedItemsList: React.FC<FeaturedItemsListProps> = ({
  type,
  manager,
  onMoveUp,
  onMoveDown,
  onToggleVisibility,
  onRemove,
  getPositionIcon,
  getPositionColor,
  getItemDisplayName,
  onOpenModal,
}) => {
  if (manager.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 text-[#00B6F0] animate-spin" />
      </div>
    );
  }

  if (manager.featuredItems.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 bg-[#00B6F0]/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <Star className="h-8 w-8 text-[#00B6F0]" />
        </div>
        <h3 className="text-lg font-semibold text-white mb-2">
          Nenhum {type} em destaque
        </h3>
        <p className="text-gray-400 mb-4">
          Adicione até 3 {type}s para destacar na plataforma
        </p>
        <Button
          onClick={onOpenModal}
          className="bg-[#00B6F0] text-black hover:bg-[#0077B6]"
        >
          <Plus className="h-4 w-4 mr-2" />
          Adicionar {type}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header com botão para adicionar mais itens */}
      <div className="flex items-center justify-between">
        <div className="text-white">
          <h4 className="font-semibold">{manager.featuredItems.length} de 3 {type}s em destaque</h4>
          <p className="text-sm text-gray-400">Gerencie os destaques e suas posições</p>
        </div>
        {manager.featuredItems.length < 3 && (
          <Button
            onClick={onOpenModal}
            className="bg-[#00B6F0] text-black hover:bg-[#0077B6]"
            size="sm"
          >
            <Plus className="h-4 w-4 mr-2" />
            Adicionar {type}
          </Button>
        )}
      </div>

      {/* Lista de itens em destaque */}
      {manager.featuredItems.map((item) => (
        <Card key={item.id} className="bg-[#242C37] border-[#2A3749]">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {/* Position Badge */}
                <div className={`${getPositionColor(item.position)} p-2 rounded-lg flex items-center gap-2 text-white font-bold`}>
                  {getPositionIcon(item.position)}
                  #{item.position}
                </div>

                {/* Item Info */}
                <div>
                  <p className="text-white font-medium">
                    {getItemDisplayName(item, type)}
                  </p>
                  <p className="text-gray-400 text-sm">
                    ID: {item.item_id.slice(0, 8)}...
                  </p>
                  <div className="flex items-center gap-4 mt-1">
                    <Badge variant={item.featured_on_home ? "default" : "secondary"}>
                      Home: {item.featured_on_home ? 'Ativo' : 'Inativo'}
                    </Badge>
                    <Badge variant={item.featured_on_page ? "default" : "secondary"}>
                      Página: {item.featured_on_page ? 'Ativo' : 'Inativo'}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2">
                {/* Move Up/Down */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onMoveUp(item.id, item.position)}
                  disabled={item.position <= 1 || manager.isSwapping}
                  className="bg-transparent border-[#2A3749] text-gray-400 hover:bg-[#2A3749]"
                >
                  <ArrowUp className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onMoveDown(item.id, item.position)}
                  disabled={item.position >= 3 || manager.isSwapping}
                  className="bg-transparent border-[#2A3749] text-gray-400 hover:bg-[#2A3749]"
                >
                  <ArrowDown className="h-4 w-4" />
                </Button>

                {/* Toggle Home Visibility */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onToggleVisibility(item.id, 'featured_on_home', item.featured_on_home)}
                  disabled={manager.isUpdating}
                  className="bg-transparent border-[#2A3749] text-gray-400 hover:bg-[#2A3749]"
                  title={`${item.featured_on_home ? 'Ocultar' : 'Mostrar'} na Home`}
                >
                  {item.featured_on_home ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </Button>

                {/* Toggle Page Visibility */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onToggleVisibility(item.id, 'featured_on_page', item.featured_on_page)}
                  disabled={manager.isUpdating}
                  className="bg-transparent border-[#2A3749] text-gray-400 hover:bg-[#2A3749]"
                  title={`${item.featured_on_page ? 'Ocultar' : 'Mostrar'} na Página`}
                >
                  {item.featured_on_page ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                </Button>

                {/* Remove */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onRemove(item.id)}
                  disabled={manager.isRemoving}
                  className="bg-transparent border-red-800 text-red-400 hover:bg-red-900/20"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

// Componente Modal de Seleção de Mentores
const MentorSelectionModal: React.FC<{
  onSelect: (mentorId: string) => void;
  isOpen: boolean;
  onClose: () => void;
}> = ({ onSelect, isOpen, onClose }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [gameFilter, setGameFilter] = useState("all");
  
  const { data: games } = useGames();
  const { data: mentors, isLoading, error } = useMentors(searchTerm, gameFilter);

  const handleSelect = (mentorId: string) => {
    onSelect(mentorId);
    onClose();
    setSearchTerm("");
    setGameFilter("all");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Selecionar Mentor para Destaque</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Filtros */}
          <div className="flex gap-4">
            <Input
              placeholder="Buscar por nome ou nickname..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1"
            />
            <Select value={gameFilter} onValueChange={setGameFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filtrar por jogo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os jogos</SelectItem>
                {games?.map(game => (
                  <SelectItem key={game.id} value={game.name}>
                    {game.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Lista de Mentores */}
          <div className="grid gap-3 max-h-96 overflow-y-auto">
            {error ? (
              <Alert>
                <AlertDescription>
                  Erro ao carregar mentores: {error.message}
                </AlertDescription>
              </Alert>
            ) : isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))
            ) : mentors?.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Nenhum mentor encontrado
              </div>
            ) : (
              mentors?.map(mentor => (
                <Card key={mentor.id} className="cursor-pointer hover:bg-gray-50 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {mentor.image_url && (
                          <img 
                            src={mentor.image_url} 
                            alt={mentor.full_name}
                            className="w-12 h-12 rounded-full object-cover"
                          />
                        )}
                        <div>
                          <h4 className="font-semibold">{mentor.full_name}</h4>
                          <p className="text-sm text-gray-600">@{mentor.nickname}</p>
                          {mentor.game_name && (
                            <Badge variant="secondary" className="text-xs">
                              {mentor.game_name}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">
                          {mentor.total_sessions} sessões
                        </p>
                        <Button 
                          size="sm" 
                          onClick={() => handleSelect(mentor.id)}
                        >
                          Selecionar
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Componente Modal de Seleção de Mentorias
const MentoriaSelectionModal: React.FC<{
  onSelect: (mentoriaId: string) => void;
  isOpen: boolean;
  onClose: () => void;
}> = ({ onSelect, isOpen, onClose }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [gameFilter, setGameFilter] = useState("all");
  
  const { data: games } = useGames();
  const { data: mentorias, isLoading, error } = useMentorias(searchTerm, gameFilter);

  const handleSelect = (mentoriaId: string) => {
    onSelect(mentoriaId);
    onClose();
    setSearchTerm("");
    setGameFilter("all");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Selecionar Mentoria para Destaque</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Filtros */}
          <div className="flex gap-4">
            <Input
              placeholder="Buscar por título..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1"
            />
            <Select value={gameFilter} onValueChange={setGameFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filtrar por jogo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os jogos</SelectItem>
                {games?.map(game => (
                  <SelectItem key={game.id} value={game.name}>
                    {game.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Lista de Mentorias */}
          <div className="grid gap-3 max-h-96 overflow-y-auto">
            {error ? (
              <Alert>
                <AlertDescription>
                  Erro ao carregar mentorias: {error.message}
                </AlertDescription>
              </Alert>
            ) : isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-24 w-full" />
              ))
            ) : mentorias?.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Nenhuma mentoria encontrada
              </div>
            ) : (
              mentorias?.map(mentoria => (
                <Card key={mentoria.id} className="cursor-pointer hover:bg-gray-50 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <h4 className="font-semibold">{mentoria.title}</h4>
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {mentoria.description}
                        </p>
                        <div className="flex items-center gap-2 mt-2">
                          {mentoria.game_name && (
                            <Badge variant="secondary" className="text-xs">
                              {mentoria.game_name}
                            </Badge>
                          )}
                          {mentoria.mentor_name && (
                            <span className="text-xs text-gray-500">
                              por {mentoria.mentor_name}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right ml-4">
                        <p className="font-semibold text-green-600">
                          R$ {mentoria.price.toFixed(2)}
                        </p>
                        <p className="text-sm text-gray-600">
                          {mentoria.total_purchases} compras
                        </p>
                        <Button 
                          size="sm" 
                          onClick={() => handleSelect(mentoria.id)}
                          className="mt-2"
                        >
                          Selecionar
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Componente Modal de Seleção de Comunidades
const ComunidadeSelectionModal: React.FC<{
  onSelect: (comunidadeId: string) => void;
  isOpen: boolean;
  onClose: () => void;
}> = ({ onSelect, isOpen, onClose }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [gameFilter, setGameFilter] = useState("all");
  
  const { data: games } = useGames();
  const { data: comunidades, isLoading, error } = useComunidades(searchTerm, gameFilter);

  const handleSelect = (comunidadeId: string) => {
    onSelect(comunidadeId);
    onClose();
    setSearchTerm("");
    setGameFilter("all");
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Selecionar Comunidade para Destaque</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Filtros */}
          <div className="flex gap-4">
            <Input
              placeholder="Buscar por nome..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="flex-1"
            />
            <Select value={gameFilter} onValueChange={setGameFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filtrar por jogo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos os jogos</SelectItem>
                {games?.map(game => (
                  <SelectItem key={game.id} value={game.name}>
                    {game.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Lista de Comunidades */}
          <div className="grid gap-3 max-h-96 overflow-y-auto">
            {error ? (
              <Alert>
                <AlertDescription>
                  Erro ao carregar comunidades: {error.message}
                </AlertDescription>
              </Alert>
            ) : isLoading ? (
              Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-20 w-full" />
              ))
            ) : comunidades?.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Nenhuma comunidade encontrada
              </div>
            ) : (
              comunidades?.map(comunidade => (
                <Card key={comunidade.id} className="cursor-pointer hover:bg-gray-50 transition-colors">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {comunidade.image_url && (
                          <img 
                            src={comunidade.image_url} 
                            alt={comunidade.name}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        )}
                        <div>
                          <h4 className="font-semibold">{comunidade.name}</h4>
                          <p className="text-sm text-gray-600 line-clamp-1">
                            {comunidade.description}
                          </p>
                          {comunidade.game_name && (
                            <Badge variant="secondary" className="text-xs mt-1">
                              {comunidade.game_name}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">
                          {comunidade.member_count} membros
                        </p>
                        <Button 
                          size="sm" 
                          onClick={() => handleSelect(comunidade.id)}
                        >
                          Selecionar
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FeaturedItemsManager;